import { StartPaymentProcessDTO } from 'api/simple_payment'
import { useToken } from 'hooks/token'
import { useSelectedSeats } from 'hooks/user/useSelling'
import { useTotalPriceTickets } from 'hooks/user/useSelling/useTotalPriceTickets'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { useCallback, useEffect, useState } from 'react'
import { EventDetails } from 'types/Event'

type PaymentHookProps = {
    currentEvent: EventDetails
    isComplimentary?: boolean
    isAdmin?: boolean
    getCustomerData: () => StartPaymentProcessDTO['customer']
    getInvoiceData: () => StartPaymentProcessDTO['invoice']
    startProcess: (req: StartPaymentProcessDTO) => Promise<any>
    urlPrefix: string
    seatIdsOverride?: string[]
}

export const usePayment = ({
    currentEvent,
    isComplimentary = false,
    isAdmin = false,
    getCustomerData,
    getInvoiceData,
    startProcess,
    urlPrefix,
    seatIdsOverride,
}: PaymentHookProps) => {
    const { selectedSeats, changeSelectedSeats, clearSelectedSeats } = useSelectedSeats(currentEvent.event_id)
    const { sessionId } = useToken()
    const { data: ticketsLockedByUser } = useTicketsLockedByUser(currentEvent.event_id, sessionId ?? '')
    const [selectedTicketIds, setSelectedTickets] = useState<string[]>([])
    const [isLoadingPayment, setIsLoadingPayment] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)

    const totalPrice = useTotalPriceTickets(ticketsLockedByUser, isComplimentary)

    const getTickets = useCallback(async () => {
        if (!currentEvent?.event_id || !sessionId) return
        try {
            setSelectedTickets(ticketsLockedByUser?.map((t) => t.ticket_id)!)
        } catch (err) {
            console.error('Failed to fetch tickets:', err)
        }
    }, [currentEvent?.event_id, sessionId, ticketsLockedByUser])

    useEffect(() => {
        if (selectedSeats.length > 0 && currentEvent?.tickets) {
            getTickets()
        }
    }, [selectedSeats, currentEvent?.tickets, getTickets])

    const persistPaymentData = (paymentRequest: StartPaymentProcessDTO) => {
        // Use sessionStorage to prevent stale payment data across sessions
        sessionStorage.setItem('lastPaymentData', JSON.stringify(paymentRequest))
    }

    const buildPaymentRequest = (): StartPaymentProcessDTO => {
        const customerData = getCustomerData()
        const invoiceData = getInvoiceData()

        const request: StartPaymentProcessDTO = {
            isComplimentary,
            total: totalPrice,
            url: `${urlPrefix}/${currentEvent.event_id}?seats=${seatIdsOverride ? seatIdsOverride.join(',') : selectedSeats.join(',')}`,
            customer: customerData,
            invoice: invoiceData,
            event: {
                eventId: currentEvent.event_id,
                ticketIds: selectedTicketIds,
            },
        }

        persistPaymentData(request)
        return request
    }

    const handlePayment = async () => {
        setIsLoadingPayment(true)
        setPaymentError(null)

        if (isComplimentary && !isAdmin) {
            setPaymentError('Tiszteletjegyek kiadása kizárólag adminisztrátori jogosultsággal lehetséges.')
            setIsLoadingPayment(false)
            return
        }

        // CRITICAL: Prevent double submission
        if (isLoadingPayment) {
            console.warn('Payment already in progress, ignoring duplicate submission')
            return
        }

        // CRITICAL: Verify tickets are still locked before payment
        if (!ticketsLockedByUser || ticketsLockedByUser.length === 0) {
            setPaymentError('A kiválasztott jegyek már nem érhetők el. Kérjük, válasszon újra ülőhelyeket.')
            setIsLoadingPayment(false)
            return
        }

        // Verify all selected tickets are still in our locked tickets
        const lockedTicketIds = new Set(ticketsLockedByUser.map((t) => t.ticket_id))
        const missingTickets = selectedTicketIds.filter((id) => !lockedTicketIds.has(id))
        if (missingTickets.length > 0) {
            console.error('Some tickets are no longer locked:', missingTickets)
            setPaymentError('Egyes kiválasztott jegyek már nem érhetők el. Kérjük, frissítse az oldalt és válasszon újra.')
            setIsLoadingPayment(false)
            return
        }

        try {
            const paymentRequest = buildPaymentRequest()

            const response = await startProcess(paymentRequest)
            const transactionId = response.payment.transactionId
            sessionStorage.setItem('transaction_id', transactionId?.toString())

            clearSelectedSeats()
            window.location.href = response.payment.paymentUrl
        } catch (error: any) {
            console.error('Payment initiation failed:', error)

            // Parse backend error messages
            const errorMessage = error?.response?.data?.message || error?.message || ''

            if (errorMessage.includes('no longer available') || errorMessage.includes('már nem elérhető')) {
                setPaymentError(
                    'A kiválasztott jegyek már nem érhetők el. Egy másik vásárló most vásárolta meg őket. Kérjük, válasszon másik ülőhelyeket.',
                )
            } else if (errorMessage.includes('reservation has expired') || errorMessage.includes('foglalás lejárt')) {
                setPaymentError('A foglalás időtúllépés miatt lejárt. Kérjük, válassza ki újra az ülőhelyeket.')
            } else if (errorMessage.includes('locked by')) {
                setPaymentError('A jegyeket egy másik vásárló foglalja. Kérjük, válasszon másik ülőhelyeket.')
            } else {
                setPaymentError('A fizetés jelenleg nem elérhető. Kérjük, próbálja meg újra később.')
            }
        } finally {
            setIsLoadingPayment(false)
        }
    }

    return {
        selectedSeats,
        selectedTicketIds,
        totalPrice,
        isLoadingPayment,
        paymentError,
        handlePayment,
        clearSelectedSeats,
        changeSelectedSeats,
        buildPaymentRequest,
    }
}
